import { doc, getDoc } from 'firebase/firestore';
import { getDb } from './firestoreDb';
import { verifyEmailAsync } from '../../utilities/emailEncryption';

/**
 * Shared utility for getting user email from encrypted Organisations data
 * Handles encrypted email in Organisations[orgId].email with fallback to Users.email
 */

export interface UserEmailInfo {
  email: string;
  name: string;
  source: string;
}

type UserEmailSource = {
  email?: string;
  displayName?: string;
  userProfile?: { userName?: string };
  Organisations?: Record<string, { email?: string } | undefined>;
};

async function decryptIfNeeded(value: string): Promise<string> {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.includes('@')) return trimmed;
  try {
    const decrypted = await verifyEmailAsync(trimmed);
    return decrypted?.trim() && decrypted.includes('@') ? decrypted.trim() : '';
  } catch {
    return '';
  }
}

/**
 * Resolve a sendable email from a loaded user document.
 * Prefers a plaintext or encrypted top-level email, then encrypted org membership emails.
 */
export async function resolveDecryptedEmailFromUserData(
  userData: UserEmailSource
): Promise<{ email: string; name: string } | null> {
  const name = userData.userProfile?.userName || userData.displayName || '';

  if (userData.email?.trim()) {
    const email = await decryptIfNeeded(userData.email);
    if (email) return { email, name: name || email };
  }

  if (userData.Organisations && typeof userData.Organisations === 'object') {
    for (const org of Object.values(userData.Organisations)) {
      if (!org?.email?.trim()) continue;
      const email = await decryptIfNeeded(org.email);
      if (email) return { email, name: name || email };
    }
  }

  return null;
}

/**
 * Get user email from user document
 * Handles encrypted email in Organisations[orgId].email with fallback to Users.email
 */
export async function getUserEmail(
  userId: string,
  organisationId: string
): Promise<UserEmailInfo | null> {
  try {
    const userDoc = await getDoc(doc(getDb(), 'Users', userId));
    if (!userDoc.exists()) {
      console.error(`❌ User document not found for ID: ${userId}`);
      return null;
    }

    const userData = userDoc.data();
    const organisations = userData.Organisations || {};
    
    let userEmail = '';
    let emailSource = '';
    
    // Get email from Organisations[orgId].email (encrypted) for this organisation
    if (organisations[organisationId]?.email) {
      const encryptedEmail = organisations[organisationId].email;
      try {
        userEmail = await verifyEmailAsync(encryptedEmail);
        emailSource = `Organisations[${organisationId}].email (decrypted)`;
        console.log(`📧 User ${userId}: Using decrypted email from Organisations[${organisationId}].email -> ${userEmail}`);
      } catch (decryptError) {
        console.error(`❌ Failed to decrypt email for user ${userId}:`, decryptError);
        userEmail = userData.email || '';
        emailSource = 'Users.email (fallback - decryption failed)';
        console.warn(`⚠️  User ${userId}: Using fallback email from Users.email -> ${userEmail}`);
      }
    } else {
      userEmail = userData.email || '';
      emailSource = 'Users.email (fallback - no Organisations email)';
      console.warn(`⚠️  User ${userId}: No email in Organisations[${organisationId}], using fallback from Users.email -> ${userEmail}`);
    }
    
    const userName = userData.userProfile?.userName || userData.displayName || 'User';

    if (!userEmail) {
      console.error(`❌ No email found for user ${userId} (${userName})`);
      return null;
    }

    return { email: userEmail, name: userName, source: emailSource };
  } catch (error) {
    console.error(`Error getting user email for ${userId}:`, error);
    return null;
  }
}
